import sendIcon from '@/assets/images/play/send.svg';
import tipIcon from '@/assets/images/play/tip.svg';
import { Skeleton } from '@/components/shadcn/skeleton';
import { getAgentApi } from '@/gql';
import { Agent } from '@/gql/agent/__generated__/request';
import { TokenDetail } from '@/gql/pump/__generated__/request';
import { useAgentChats } from '@/hooks/useAgentChats';
import useCopyClipboard from '@/hooks/useCopy';
import application from '@/stores/application';
import user from '@/stores/user';
import Alert from '@/utils/components/alert';
import RefreshIcon from '@mui/icons-material/Refresh';
import makeBlockie from 'ethereum-blockies-base64';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

const Chat = ({
    agent,
    tokenDetail,
    chatLimit,
    chatCount,
    totalChatLimit,
    totalChatCount,
    refetchChatLimit,
    refetchAgentExtraInfo,
}: {
    agent: Agent;
    tokenDetail?: TokenDetail;
    chatLimit: number;
    chatCount: number;
    totalChatLimit: number;
    totalChatCount: number;
    refetchChatLimit: () => void;
    refetchAgentExtraInfo: () => void;
}) => {
    const [, setCopied] = useCopyClipboard();
    const { address } = user;
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [lastChatId, setLastChatId] = useState(0);
    const [chatList, setChatList] = useState<any[]>([]);
    const [question, setQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const {
        chats,
        refetch: refetchChats,
        loading: chatsLoading,
    } = useAgentChats(agent.uid);

    useEffect(() => {
        if (chats && chats.length > 0) {
            if (chats[chats.length - 1].id > lastChatId) {
                refetchChatLimit();
                setLastChatId(chats[chats.length - 1].id);
                const newChatList = chats.filter(
                    (chat) => chat.id > lastChatId,
                );
                setChatList([...chatList, ...newChatList]);
            }
        }
    }, [chats, lastChatId]);

    useEffect(() => {
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }
        }, 100);
    }, [chatList]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            chat(question);
        }
    };

    const chat = async (content: string) => {
        if (content === '' || isLoading) return;
        if (totalChatCount >= totalChatLimit) {
            Alert({
                type: 'error',
                text: "The agent's daily test message quota used up",
            });
            return;
        }
        if (chatCount >= chatLimit) {
            Alert({
                type: 'error',
                text: 'You have reached the daily chat limit',
            });
            return;
        }
        setQuestion('');
        setIsLoading(true);
        setChatList([
            ...chatList,
            {
                id: new Date().getTime(),
                content,
                role: 'user',
                type: 'normal',
            },
            {
                id: new Date().getTime() + 1,
                content: 'Thinking...',
                role: 'assistant',
                type: 'thinking',
            },
        ]);
        try {
            const api = getAgentApi();
            const res = await api.sendMessage({
                agentUid: agent.uid,
                message: content,
            });
            if (res.sendMessage) {
                setChatList((prev) => {
                    const newChatList = [...prev];
                    newChatList[newChatList.length - 2].id =
                        res.sendMessage.id - 1;
                    newChatList[newChatList.length - 1] = res.sendMessage;
                    return newChatList;
                });
                setLastChatId(res.sendMessage.id);
            }
        } catch (error) {
            console.error(error);
            setChatList((prev) => {
                const newChatList = [...prev];
                newChatList[newChatList.length - 1].content =
                    'Oops...Something wrong';
                newChatList[newChatList.length - 1].type = 'error';
                return newChatList;
            });
        } finally {
            setIsLoading(false);
            refetchChatLimit();
            refetchAgentExtraInfo();
        }
    };

    const ChatItem = ({ item }: { item: any }) => {
        return (
            <div className="flex flex-col gap-[20px] max-md:gap-[15px]">
                <div className="flex gap-[20px] max-md:gap-[15px]">
                    {item.role === 'assistant' && tokenDetail?.imgUrl && (
                        <img
                            src={tokenDetail.imgUrl}
                            className="w-[40px] h-[40px] rounded-full"
                        />
                    )}
                    <div
                        className={`px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] text-[12px] max-md:text-[11px] rounded-[16px] border ${
                            item.role === 'user'
                                ? 'bg-black text-white border-black ml-auto'
                                : 'bg-white border-primary/10'
                        } ${
                            item.type === 'error'
                                ? 'border-error text-error bg-error/10'
                                : ''
                        }`}
                    >
                        {item.type === 'thinking' && (
                            <RefreshIcon className="w-[16px] max-md:w-[14px] animate-spin" />
                        )}
                        <div>
                            <ReactMarkdown>{item.content}</ReactMarkdown>
                        </div>
                    </div>
                    {item.role === 'user' && address && (
                        <img
                            src={makeBlockie(address)}
                            className="w-[40px] h-[40px] rounded-full"
                        />
                    )}
                </div>
            </div>
        );
    };

    const ChatSkeleton = () => {
        return (
            <div className="flex flex-col gap-[20px] max-md:gap-[15px]">
                <div className="flex items-center gap-[20px] max-md:gap-[15px] justify-end">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-black border-black">
                        <Skeleton className="w-[150px] h-[20px] max-md:w-[100px] max-md:h-[16px] bg-white/20" />
                    </div>
                </div>
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-white border-primary/10">
                        <Skeleton className="w-[200px] h-[20px] max-md:w-[150px] max-md:h-[16px]" />
                    </div>
                </div>

                <div className="flex items-center gap-[20px] max-md:gap-[15px] justify-end">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-black border-black">
                        <Skeleton className="w-[150px] h-[20px] max-md:w-[100px] max-md:h-[16px] bg-white/20" />
                    </div>
                </div>
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-white border-primary/10">
                        <Skeleton className="w-[300px] h-[20px] max-md:w-[200px] max-md:h-[16px]" />
                    </div>
                </div>

                <div className="flex items-center gap-[20px] max-md:gap-[15px] justify-end">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-black border-black">
                        <Skeleton className="w-[150px] h-[20px] max-md:w-[100px] max-md:h-[16px] bg-white/20" />
                    </div>
                </div>
                <div className="flex items-center gap-[20px] max-md:gap-[15px]">
                    <div className="px-[20px] max-md:px-[15px] flex items-center leading-[20px] gap-[10px] py-[10px] max-md:py-[8px] rounded-[16px] border bg-white border-primary/10">
                        <Skeleton className="w-[300px] h-[20px] max-md:w-[200px] max-md:h-[16px]" />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="absolute top-[20px] right-[20px] px-[10px] py-[11px] max-md:rounded-[8px] gap-[10px] max-md:gap-[5px] flex items-center justify-center max-md:right-[20px] max-md:px-[6px] max-md:py-[7px] max-md:text-[10px] bg-white rounded-[12px] text-[12px]">
                <img
                    src={tipIcon}
                    className="w-[16px] h-[16px] max-md:w-[10px] max-md:h-[10px]"
                />
                Show up to 10 messages
            </div>
            <div
                ref={scrollContainerRef}
                className="flex flex-col gap-[20px] max-md:gap-[15px] w-full h-full pb-[120px] pt-[60px] overflow-auto max-md:pb-[50px]"
            >
                {chatsLoading && chatList.length === 0 ? (
                    <ChatSkeleton />
                ) : (
                    chatList.map((item) => (
                        <ChatItem key={item.id} item={item} />
                    ))
                )}
                <div
                    className={`px-[40px] max-md:px-[20px] p-[20px] max-md:p-[15px] absolute left-0 right-0 bottom-0 transition-all bg-background ${
                        application.isExploreNavDrawerOpen
                            ? 'md:pl-[280px]'
                            : 'md:pl-[40px]'
                    } ${
                        application.isLazbubuDrawerOpen
                            ? 'md:pr-[390px]'
                            : 'md:pr-[40px]'
                    }`}
                >
                    <div className="flex gap-[20px] flex-wrap max-md:gap-[15px] max-sm:gap-[10px] h-[52px] max-md:h-[30px]">
                        <div
                            className={`flex-1 flex gap-[10px] max-md:gap-[8px] items-center border px-[20px] max-md:px-[15px] max-sm:px-[12px] h-full max-md:h-[30px] bg-white border-border rounded-[16px] ${
                                chatCount >= chatLimit ||
                                totalChatCount >= totalChatLimit
                                    ? 'opacity-50'
                                    : ''
                            }`}
                        >
                            <input
                                type="text"
                                placeholder={`${
                                    chatCount >= chatLimit
                                        ? 'You have reached the daily chat limit'
                                        : totalChatCount >= totalChatLimit
                                        ? "The agent's daily test message quota used up"
                                        : `Talk to ${agent?.name}...`
                                }`}
                                className="flex-1 text-[12px] max-md:text-[11px] max-sm:text-[10px] disabled:bg-transparent"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={
                                    chatCount >= chatLimit ||
                                    totalChatCount >= totalChatLimit
                                }
                            />
                            <button
                                className={`bg-black h-[32px] w-[32px] max-md:h-[20px] max-md:w-[20px] text-white flex items-center justify-center rounded-[10px] max-md:rounded-[5px]`}
                                onClick={() => chat(question)}
                                disabled={
                                    chatCount >= chatLimit ||
                                    totalChatCount >= totalChatLimit
                                }
                            >
                                <img
                                    src={sendIcon}
                                    className="w-[16px] h-[16px] max-md:w-[10px] max-md:h-[10px]"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default observer(Chat);
